/**
 * Step 1 of the loop: register.
 *
 * Two notes on what this deliberately does not do.
 *
 * Better Auth's `user.name` column is NOT NULL, so something is always sent:
 * the optional nickname, or an empty string. The nickname is asked for without
 * a placeholder or any hint that it is expected; registration works without
 * it. It is shown to the reader in the header, and beside any review they
 * write, which the privacy page says.
 *
 * An address that already has an account produces exactly the same screen as a
 * new one, because showing otherwise would turn this form into a way of asking
 * whether somebody has an account here.
 *
 * This header used to say "Better Auth reports USER_ALREADY_EXISTS". It does
 * not, in this configuration. A duplicate `/sign-up/email` answers 200 with a
 * fabricated user id, writes nothing, and leaves the existing account's
 * password and `emailVerified` untouched — the library is doing the same
 * anti-enumeration thing we are. Measured against 1.7.5, not assumed.
 *
 * So the `error.email_taken` branch below never fires today. It stays anyway,
 * and it is not dead code: the moment anyone adds a server-side duplicate check
 * — the obvious "helpful" change — it becomes the only thing still holding that
 * criterion up. `auth-security.spec.ts` covers the criterion from the outside
 * and was verified against both layers being broken.
 */

import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { NICKNAME_MAX_LENGTH, registerInputSchema } from '../../shared/auth-schemas.ts'
import type { MessageKey } from '../../shared/message-keys.ts'
import { safeReturnPath, signInAfterRegistration } from '../../shared/return-path.ts'
import { Alert } from '../components/Alert.tsx'
import { Field } from '../components/Field.tsx'
import { Turnstile } from '../components/Turnstile.tsx'
import { useI18n } from '../i18n/index.tsx'
import { authClient } from '../lib/auth-client.ts'
import { messageKeyFor } from '../lib/api.ts'
import { useClientConfig } from '../lib/use-client-config.ts'

export function Register() {
  const { t, locale } = useI18n()
  const config = useClientConfig()
  // The page the reader came from (registerPath), carried through the
  // verification link to the sign-in at the end (#105). Null when they came
  // to registration directly.
  const [params] = useSearchParams()
  const returnTo = safeReturnPath(params.get('next'))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [nickname, setNickname] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileResets, setTurnstileResets] = useState(0)
  const [error, setError] = useState<MessageKey | null>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    const parsed = registerInputSchema.safeParse({ email, password, turnstileToken, nickname })
    if (!parsed.success) {
      setError((parsed.error.issues[0]?.message ?? 'error.bad_request') as MessageKey)
      return
    }

    setBusy(true)
    const { error: failure } = await authClient.signUp.email({
      email: parsed.data.email,
      password: parsed.data.password,
      name: parsed.data.nickname,
      callbackURL: `${window.location.origin}/verify${returnTo === null ? '' : `?next=${encodeURIComponent(returnTo)}`}`,
      fetchOptions: {
        headers: { 'x-turnstile-token': parsed.data.turnstileToken },
      },
    })
    setBusy(false)

    if (!failure) {
      setSent(true)
      return
    }

    const key = messageKeyFor(failure)
    if (key === 'error.email_taken') {
      // Same screen as success, on purpose — unreachable today, load-bearing the
      // day a server-side duplicate check appears. See the note at the top.
      setSent(true)
      return
    }

    setTurnstileToken('')
    setTurnstileResets((n) => n + 1)
    setError(key)
  }

  if (sent) {
    return (
      <section className="panel">
        <h1>{t('register.sent.title')}</h1>
        <p>{t('register.sent.body')}</p>
        <Link to={signInAfterRegistration(returnTo)}>{t('signin.title')}</Link>
      </section>
    )
  }

  return (
    <section className="panel">
      <h1>{t('register.title')}</h1>
      <p>{t('register.lead')}</p>

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          label={t('register.email.label')}
          hint={t('register.email.hint')}
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
          }}
        />

        <Field
          label={t('register.password.label')}
          hint={t('register.password.hint')}
          type="password"
          name="password"
          autoComplete="new-password"
          minLength={config?.passwordMinLength ?? 12}
          required
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
          }}
        />

        <Field
          label={t('register.nickname.label')}
          hint={t('register.nickname.hint')}
          type="text"
          name="nickname"
          autoComplete="nickname"
          maxLength={NICKNAME_MAX_LENGTH}
          value={nickname}
          onChange={(event) => {
            setNickname(event.target.value)
          }}
        />

        {config ? (
          <Turnstile
            siteKey={config.turnstileSiteKey}
            locale={locale}
            onToken={setTurnstileToken}
            resetSignal={turnstileResets}
          />
        ) : null}

        {error ? <Alert tone="error">{t(error)}</Alert> : null}

        <button type="submit" className="button" disabled={busy}>
          {busy ? t('register.submitting') : t('register.submit')}
        </button>
      </form>

      <Link to="/signin">{t('register.have_account')}</Link>
    </section>
  )
}
